// src/utils/idPatterns.ts
var idPatterns = {
  action: /^act_/,
  campaign: /^cpn_/,
  dimension: /^dim_/,
  event: /^gev_/,
  goal: /^gol_/,
  rightBar: /^rbr_/,
  rightBarVariant: /^rbv_/,
  question: /^raq_/,
  questionOption: /^rqo_/,
  widget: /^wdg_/,
  flow: /^ofn_/,
  offer: /^ofr_/,
  segment: /^(seg_\w+|\$everyone)$/,
  variant: /^var_/,
  phrase: /^phr_/
};
var tempIdPatterns = {
  dimension: /^dim_tmp_\d+$/,
  segment: /^seg_tmp_\d+_\d+$/,
  question: /^raq_tmp_\d+$/,
  questionOption: /^rqo_tmp_\d+_\d+$/,
  offer: /^ofr_tmp_\d+$/,
  flow: /^ofn_tmp_\d+$/,
  widget: /^wdg_tmp_\d+$/,
  phrase: /^phr_tmp_\d+$/,
  flowNode: /^node_tmp_\d+$/,
  campaign: /^cpn_tmp_\d+$/,
  variant: /^var_tmp_\d+$/,
  action: /^act_tmp_\d+$/
};
var idOrTempPatterns = {
  dimension: /^dim_(tmp_\d+|[a-zA-Z0-9]+)$/,
  segment: /^(seg_(tmp_\d+_\d+|[a-zA-Z0-9_]+)|\$everyone)$/,
  question: /^raq_(tmp_\d+|[a-zA-Z0-9]+)$/,
  questionOption: /^rqo_(tmp_\d+_\d+|[a-zA-Z0-9]+)$/,
  offer: /^ofr_(tmp_\d+|[a-zA-Z0-9]+)$/,
  flow: /^ofn_(tmp_\d+|[a-zA-Z0-9]+)$/,
  widget: /^wdg_(tmp_\d+|[a-zA-Z0-9]+)$/,
  phrase: /^phr_(tmp_\d+|[a-zA-Z0-9]+)$/,
  campaign: /^cpn_(tmp_\d+|[a-zA-Z0-9]+)$/,
  variant: /^var_(tmp_\d+|[a-zA-Z0-9]+)$/,
  action: /^act_(tmp_\d+|[a-zA-Z0-9]+)$/
};
var idPrefixes = {
  action: "act_",
  campaign: "cpn_",
  dimension: "dim_",
  event: "gev_",
  goal: "gol_",
  rightBar: "rbr_",
  rightBarVariant: "rbv_",
  question: "raq_",
  questionOption: "rqo_",
  widget: "wdg_",
  flow: "ofn_",
  offer: "ofr_",
  segment: "seg_",
  variant: "var_",
  phrase: "phr_"
};
function isRealId(id, resourceType) {
  const tempResourceType = resourceType;
  if (tempResourceType in tempIdPatterns && tempIdPatterns[tempResourceType].test(id)) {
    return false;
  }
  const pattern = idPatterns[resourceType];
  return pattern.test(id);
}
function isTempId(id, resourceType) {
  const pattern = tempIdPatterns[resourceType];
  return pattern.test(id);
}
function isIdOrTempId(id, resourceType) {
  const pattern = idOrTempPatterns[resourceType];
  return pattern.test(id);
}
function getResourceTypeFromId(id) {
  for (const [type, prefix] of Object.entries(idPrefixes)) {
    if (id.startsWith(prefix)) {
      return type;
    }
  }
  if (id === "$everyone") {
    return "segment";
  }
  return null;
}

// src/utils/tempId.ts
function createTempIdCounters() {
  return {
    dimension: 0,
    segment: /* @__PURE__ */ new Map(),
    question: 0,
    questionOption: /* @__PURE__ */ new Map(),
    offer: 0,
    flow: 0,
    widget: 0,
    phrase: 0,
    flowNode: 0,
    campaign: 0,
    variant: 0,
    action: 0
  };
}
function generateTempId(resourceType, counters, parentIndex) {
  switch (resourceType) {
    case "dimension":
      return `dim_tmp_${counters.dimension++}`;
    case "segment": {
      if (parentIndex === void 0) {
        throw new Error("Segment temp IDs require a parent dimension index");
      }
      const segCount = counters.segment.get(parentIndex) ?? 0;
      counters.segment.set(parentIndex, segCount + 1);
      return `seg_tmp_${parentIndex}_${segCount}`;
    }
    case "question":
      return `raq_tmp_${counters.question++}`;
    case "questionOption": {
      if (parentIndex === void 0) {
        throw new Error("Question option temp IDs require a parent question index");
      }
      const optCount = counters.questionOption.get(parentIndex) ?? 0;
      counters.questionOption.set(parentIndex, optCount + 1);
      return `rqo_tmp_${parentIndex}_${optCount}`;
    }
    case "offer":
      return `ofr_tmp_${counters.offer++}`;
    case "flow":
      return `ofn_tmp_${counters.flow++}`;
    case "widget":
      return `wdg_tmp_${counters.widget++}`;
    case "phrase":
      return `phr_tmp_${counters.phrase++}`;
    case "flow.node":
      return `node_tmp_${counters.flowNode++}`;
    case "campaign":
      return `cpn_tmp_${counters.campaign++}`;
    case "variant":
      return `var_tmp_${counters.variant++}`;
    case "action":
      return `act_tmp_${counters.action++}`;
    default:
      throw new Error(`Unknown resource type: ${resourceType}`);
  }
}
function isTempId2(id) {
  for (const pattern of Object.values(tempIdPatterns)) {
    if (pattern.test(id)) {
      return true;
    }
  }
  return false;
}
function resolveId(id, idMap) {
  if (isTempId2(id)) {
    const realId = idMap.get(id);
    if (!realId) {
      throw new Error(`Unresolved temp ID: ${id}`);
    }
    return realId;
  }
  return id;
}
function resolveTempIds(data, idMap) {
  if (typeof data === "string") {
    return resolveId(data, idMap);
  }
  if (Array.isArray(data)) {
    return data.map((item) => resolveTempIds(item, idMap));
  }
  if (data !== null && typeof data === "object") {
    const result = {};
    for (const [key, value] of Object.entries(data)) {
      if ((key === "id" || key === "tempId" || key.endsWith("Id") || key === "indicates" || key === "segmentId") && typeof value === "string") {
        result[key] = resolveId(value, idMap);
      } else {
        result[key] = resolveTempIds(value, idMap);
      }
    }
    return result;
  }
  return data;
}
function generateRealId(resourceType) {
  const prefixKey = resourceType === "flow.node" ? null : resourceType;
  if (resourceType === "flow.node") {
    return `_${generateRandomString(12)}`;
  }
  const prefix = idPrefixes[prefixKey];
  if (!prefix) {
    throw new Error(`No prefix defined for resource type: ${resourceType}`);
  }
  return `${prefix}${generateRandomString(12)}`;
}
function generateRandomString(length) {
  const chars = "abcdefghijklmnopqrstuvwxyz0123456789";
  let result = "";
  for (let i = 0; i < length; i++) {
    result += chars.charAt(Math.floor(Math.random() * chars.length));
  }
  return result;
}
function buildIdMapFromOperations(operations) {
  const idMap = /* @__PURE__ */ new Map();
  for (const operation of operations) {
    if (operation.op === "create" && operation.tempId && operation.resource) {
      const resourceType = operation.resource;
      const realId = generateRealId(resourceType);
      idMap.set(operation.tempId, realId);
    }
  }
  return idMap;
}
function findTempIdReferences(data) {
  const refs = [];
  function walk(value) {
    if (typeof value === "string" && isTempId2(value)) {
      refs.push(value);
    } else if (Array.isArray(value)) {
      value.forEach(walk);
    } else if (value !== null && typeof value === "object") {
      Object.values(value).forEach(walk);
    }
  }
  walk(data);
  return refs;
}
function findDeclaredTempIds(data) {
  const ids = [];
  function walk(value) {
    if (value !== null && typeof value === "object") {
      if ("tempId" in value && typeof value.tempId === "string") {
        const tempId = value.tempId;
        if (isTempId2(tempId)) {
          ids.push(tempId);
        }
      }
      if (Array.isArray(value)) {
        value.forEach(walk);
      } else {
        Object.values(value).forEach(walk);
      }
    }
  }
  walk(data);
  return ids;
}
function validateTempIdReferences(operations) {
  const declaredIds = /* @__PURE__ */ new Set();
  for (const op of operations) {
    if (op.op === "create" && op.tempId) {
      declaredIds.add(op.tempId);
    }
    if (op.data) {
      for (const id of findDeclaredTempIds(op.data)) {
        declaredIds.add(id);
      }
    }
  }
  const allRefs = [];
  for (const op of operations) {
    if (op.data) {
      allRefs.push(...findTempIdReferences(op.data));
    }
  }
  const missingRefs = allRefs.filter((ref) => !declaredIds.has(ref));
  return {
    valid: missingRefs.length === 0,
    missingRefs: [...new Set(missingRefs)]
  };
}

// src/operation.schema.ts
import { z } from "zod";
var ResourceTypeSchema = z.enum([
  // Config resources
  "dimension",
  "segment",
  "question",
  "questionOption",
  "offer",
  "flow",
  "flow.node",
  "widget",
  "phrase",
  "campaign",
  "variant",
  "action",
  // Database resources
  "page",
  "email"
]);
var BaseCreateOperationSchema = z.object({
  op: z.literal("create"),
  resource: ResourceTypeSchema,
  tempId: z.string().describe("Temporary ID that will be replaced with a real ID"),
  data: z.record(z.unknown()).describe("Resource-specific data")
});
var BaseUpdateOperationSchema = z.object({
  op: z.literal("update"),
  resource: ResourceTypeSchema,
  id: z.string().describe("Real ID of the existing resource"),
  data: z.record(z.unknown()).describe("Partial resource data to merge")
});
var BaseDeleteOperationSchema = z.object({
  op: z.literal("delete"),
  resource: ResourceTypeSchema,
  id: z.string().describe("Real ID of the resource to delete")
});
var FlowNodePositionSchema = z.object({
  parent: z.string().optional().describe("Parent node UUID or temp ID"),
  after: z.string().optional().describe("Insert after this node UUID or temp ID"),
  branch: z.string().optional().describe("Branch identifier (segmentId or yes/no)")
});
var CreateFlowNodeOperationSchema = z.object({
  op: z.literal("create"),
  resource: z.literal("flow.node"),
  flowId: z.string().describe("Flow ID (real or temp)"),
  tempId: z.string().describe("Temporary ID for this node"),
  position: FlowNodePositionSchema,
  data: z.record(z.unknown()).describe("Flow node data")
});
var UpdateFlowNodeOperationSchema = z.object({
  op: z.literal("update"),
  resource: z.literal("flow.node"),
  flowId: z.string().describe("Flow ID"),
  nodeUuid: z.string().describe("UUID of the node to update"),
  data: z.record(z.unknown()).describe("Partial node data to merge")
});
var DeleteFlowNodeOperationSchema = z.object({
  op: z.literal("delete"),
  resource: z.literal("flow.node"),
  flowId: z.string().describe("Flow ID"),
  nodeUuid: z.string().describe("UUID of the node to delete")
});
var MoveFlowNodeOperationSchema = z.object({
  op: z.literal("move"),
  resource: z.literal("flow.node"),
  flowId: z.string().describe("Flow ID"),
  nodeUuid: z.string().describe("UUID of the node to move"),
  position: FlowNodePositionSchema
});
var OperationSchema = z.discriminatedUnion("op", [
  // Standard CRUD for most resources
  BaseCreateOperationSchema,
  BaseUpdateOperationSchema,
  BaseDeleteOperationSchema,
  // Special flow node operations
  MoveFlowNodeOperationSchema
]);
var StandardResourceTypes = z.enum([
  // Config resources
  "dimension",
  "segment",
  "question",
  "questionOption",
  "offer",
  "flow",
  "widget",
  "phrase",
  "campaign",
  "variant",
  "action",
  // Database resources
  "page",
  "email"
]);
var AnyOperationSchema = z.union([
  // Flow node operations
  CreateFlowNodeOperationSchema,
  UpdateFlowNodeOperationSchema,
  DeleteFlowNodeOperationSchema,
  MoveFlowNodeOperationSchema,
  // Standard operations (for non-flow.node resources)
  BaseCreateOperationSchema.extend({
    resource: StandardResourceTypes
  }),
  BaseUpdateOperationSchema.extend({
    resource: StandardResourceTypes
  }),
  BaseDeleteOperationSchema.extend({
    resource: StandardResourceTypes
  })
]);
var OperationResultSchema = z.object({
  success: z.boolean(),
  idMap: z.record(z.string()).optional().describe("Map of temp ID -> real ID"),
  changelog: z.array(
    z.object({
      operation: z.enum(["create", "update", "delete", "move"]),
      resource: z.string(),
      id: z.string(),
      tempId: z.string().optional()
    })
  ).optional(),
  error: z.string().optional(),
  failedOperation: z.number().optional().describe("Index of the failed operation")
});

// src/plan.schema.ts
import { z as z2 } from "zod";
var PlanMetadataSchema = z2.object({
  title: z2.string().optional(),
  description: z2.string().optional(),
  estimatedChanges: z2.number().optional(),
  createdAt: z2.string().optional()
});
var PlanSchema = z2.object({
  /**
   * Format version - "operations" for new CRUD format
   */
  format: z2.literal("operations").default("operations"),
  /**
   * Optional metadata about the plan
   */
  metadata: PlanMetadataSchema.optional(),
  /**
   * List of operations to execute
   */
  operations: z2.array(AnyOperationSchema)
});
var LegacyStepSchema = z2.object({
  type: z2.string(),
  name: z2.string(),
  data: z2.record(z2.unknown())
});
var LegacyPlanSchema = z2.object({
  title: z2.string().optional(),
  description: z2.string().optional(),
  steps: z2.array(LegacyStepSchema)
});
var AnyPlanSchema = z2.union([PlanSchema, LegacyPlanSchema]);
var PlanExecutionResultSchema = z2.object({
  success: z2.boolean(),
  /**
   * Map of temp ID -> real ID for all created resources
   */
  idMap: z2.record(z2.string()).optional(),
  /**
   * List of changes made
   */
  changelog: z2.array(
    z2.object({
      operation: z2.enum(["create", "update", "delete", "move"]),
      resource: z2.string(),
      id: z2.string(),
      tempId: z2.string().optional(),
      name: z2.string().optional()
    })
  ).default([]),
  /**
   * Error message if failed
   */
  error: z2.string().optional(),
  /**
   * Index of the operation that failed
   */
  failedOperationIndex: z2.number().optional(),
  /**
   * Validation errors if plan was invalid
   */
  validationErrors: z2.array(z2.string()).optional()
});
function isOperationsPlan(plan) {
  return "operations" in plan;
}
function isLegacyPlan(plan) {
  return "steps" in plan;
}

// src/resources/segment.schema.ts
import { z as z3 } from "zod";
var segmentIdPattern = /^(seg_[a-zA-Z0-9_]+|\$everyone)$/;
var segmentTempIdPattern = /^seg_tmp_\d+_\d+$/;
var SegmentIdSchema = z3.string().refine(
  (id) => segmentIdPattern.test(id) || segmentTempIdPattern.test(id),
  "Invalid segment ID format"
);
var SegmentCreateDataSchema = z3.object({
  tempId: z3.string().optional().describe("Temp ID if creating nested segments"),
  name: z3.string().min(1, "Segment name is required")
});
var SegmentDataSchema = z3.object({
  id: SegmentIdSchema,
  name: z3.string().min(1)
});
var SegmentUpdateDataSchema = z3.object({
  name: z3.string().min(1).optional()
});

// src/resources/dimension.schema.ts
import { z as z4 } from "zod";
var dimensionIdPattern = /^dim_[a-zA-Z0-9]+$/;
var dimensionTempIdPattern = /^dim_tmp_\d+$/;
var DimensionIdSchema = z4.string().refine(
  (id) => dimensionIdPattern.test(id) || dimensionTempIdPattern.test(id),
  "Invalid dimension ID format"
);
var SignalDefinitionSchema = z4.record(z4.unknown());
var SignalSchema = z4.object({
  indicates: SegmentIdSchema,
  weight: z4.enum(["strong", "weak"]).default("strong"),
  definition: SignalDefinitionSchema.optional()
});
var DimensionCreateDataSchema = z4.object({
  name: z4.string().min(1, "Dimension name is required"),
  semanticType: z4.string().nullish(),
  templateId: z4.string().optional(),
  isMultiWinning: z4.boolean().default(false),
  favorited: z4.boolean().default(false),
  syncTo: z4.string().nullish(),
  syncToCustomField: z4.string().nullish(),
  segments: z4.array(SegmentCreateDataSchema).default([]),
  signals: z4.array(SignalSchema).default([])
});
var DimensionDataSchema = z4.object({
  id: DimensionIdSchema,
  name: z4.string().min(1),
  semanticType: z4.string().nullish(),
  templateId: z4.string().optional(),
  isMultiWinning: z4.boolean().default(false),
  favorited: z4.boolean().default(false),
  accessedAt: z4.number().optional(),
  syncTo: z4.string().nullish(),
  syncToCustomField: z4.string().nullish(),
  segments: z4.array(SegmentDataSchema).default([]),
  signals: z4.array(SignalSchema).default([])
});
var DimensionUpdateDataSchema = z4.object({
  name: z4.string().min(1).optional(),
  semanticType: z4.string().nullish().optional(),
  isMultiWinning: z4.boolean().optional(),
  favorited: z4.boolean().optional(),
  syncTo: z4.string().nullish().optional(),
  syncToCustomField: z4.string().nullish().optional(),
  // Segments can be updated by adding/removing
  segments: z4.array(SegmentDataSchema).optional(),
  signals: z4.array(SignalSchema).optional()
});

// src/resources/question.schema.ts
import { z as z5 } from "zod";
var questionIdPattern = /^raq_[a-zA-Z0-9]+$/;
var questionTempIdPattern = /^raq_tmp_\d+$/;
var QuestionIdSchema = z5.string().refine(
  (id) => questionIdPattern.test(id) || questionTempIdPattern.test(id),
  "Invalid question ID format"
);
var questionOptionIdPattern = /^rqo_[a-zA-Z0-9]+$/;
var questionOptionTempIdPattern = /^rqo_tmp_\d+_\d+$/;
var QuestionOptionIdSchema = z5.string().refine(
  (id) => questionOptionIdPattern.test(id) || questionOptionTempIdPattern.test(id),
  "Invalid question option ID format"
);
var LogicDefinitionSchema = z5.record(z5.unknown());
var InputSettingsSchema = z5.object({
  placeholder: z5.string().optional(),
  validation: z5.string().optional(),
  min: z5.number().optional(),
  max: z5.number().optional(),
  step: z5.number().optional(),
  required: z5.boolean().default(false),
  minLabel: z5.string().optional(),
  maxLabel: z5.string().optional(),
  nextButtonLabel: z5.string().default("Next"),
  skipButtonLabel: z5.string().default("Skip")
});
var OtherOptionsSchema = z5.object({
  enabled: z5.boolean().default(false),
  otherLabel: z5.string().default("Other"),
  inputType: z5.enum(["text", "textarea"]).default("textarea"),
  inputPrompt: z5.string().default("Please share more"),
  inputPlaceholder: z5.string().optional(),
  nextPrompt: z5.string().default("Next"),
  syncToCustomField: z5.string().nullish()
});
var DisplayOptionsSchema = z5.record(z5.unknown()).default({});
var QuestionOptionCreateDataSchema = z5.object({
  tempId: z5.string().optional().describe("Temp ID if creating nested options"),
  segmentId: z5.union([SegmentIdSchema, z5.string()]).describe("Segment to assign when selected"),
  label: z5.string().min(1, "Option label is required"),
  conditions: LogicDefinitionSchema.default({}),
  score: z5.string().optional().describe("Score ID for scoring"),
  amount: z5.number().nullish().describe("Score amount"),
  imageUrl: z5.string().nullish()
});
var QuestionOptionDataSchema = z5.object({
  id: QuestionOptionIdSchema,
  segmentId: SegmentIdSchema,
  label: z5.string().min(1),
  conditions: LogicDefinitionSchema.default({}),
  score: z5.string().optional(),
  amount: z5.number().nullish(),
  imageUrl: z5.string().nullish()
});
var QuestionCreateDataSchema = z5.object({
  text: z5.string().min(1, "Question text is required"),
  description: z5.string().nullish(),
  displayType: z5.enum(["buttons", "dropdown", "cards"]).default("buttons"),
  displayOptions: DisplayOptionsSchema.default({}),
  dimensionId: z5.union([DimensionIdSchema, z5.string()]).nullish(),
  inputType: z5.enum(["text", "textarea", "number", "scale"]).nullish(),
  inputSettings: InputSettingsSchema.optional(),
  syncTo: z5.enum(["customField", "variable"]).nullish(),
  syncToField: z5.string().nullish(),
  isQuestion: z5.boolean().default(false),
  isRandomizingOptionsOrder: z5.boolean().default(false),
  otherOptions: OtherOptionsSchema.optional(),
  options: z5.array(QuestionOptionCreateDataSchema).default([])
});
var QuestionDataSchema = z5.object({
  id: QuestionIdSchema,
  text: z5.string().min(1),
  description: z5.string().nullish(),
  displayType: z5.enum(["buttons", "dropdown", "cards"]).default("buttons"),
  displayOptions: DisplayOptionsSchema.default({}),
  dimensionId: DimensionIdSchema.nullish(),
  inputType: z5.enum(["text", "textarea", "number", "scale"]).nullish(),
  inputSettings: InputSettingsSchema.optional(),
  syncTo: z5.enum(["customField", "variable"]).nullish(),
  syncToField: z5.string().nullish(),
  isQuestion: z5.boolean().default(false),
  isRandomizingOptionsOrder: z5.boolean().default(false),
  otherOptions: OtherOptionsSchema.optional(),
  options: z5.array(QuestionOptionDataSchema).default([])
});
var QuestionUpdateDataSchema = z5.object({
  text: z5.string().min(1).optional(),
  description: z5.string().nullish().optional(),
  displayType: z5.enum(["buttons", "dropdown", "cards"]).optional(),
  displayOptions: DisplayOptionsSchema.optional(),
  dimensionId: DimensionIdSchema.nullish().optional(),
  inputType: z5.enum(["text", "textarea", "number", "scale"]).nullish().optional(),
  inputSettings: InputSettingsSchema.optional(),
  syncTo: z5.enum(["customField", "variable"]).nullish().optional(),
  syncToField: z5.string().nullish().optional(),
  isQuestion: z5.boolean().optional(),
  isRandomizingOptionsOrder: z5.boolean().optional(),
  otherOptions: OtherOptionsSchema.optional(),
  options: z5.array(QuestionOptionDataSchema).optional()
});

// src/resources/offer.schema.ts
import { z as z6 } from "zod";
var offerIdPattern = /^ofr_[a-zA-Z0-9]+$/;
var offerTempIdPattern = /^ofr_tmp_\d+$/;
var OfferIdSchema = z6.string().refine(
  (id) => offerIdPattern.test(id) || offerTempIdPattern.test(id),
  "Invalid offer ID format"
);
var FunnelDefinitionSchema = z6.lazy(
  () => z6.object({
    type: z6.string(),
    child: z6.array(z6.unknown()).optional()
    // Other properties depend on node type
  }).passthrough()
);
var FormFieldSchema = z6.object({
  type: z6.string(),
  label: z6.string().optional(),
  placeholder: z6.string().optional(),
  required: z6.boolean().optional(),
  name: z6.string().optional(),
  options: z6.array(z6.string()).optional()
}).passthrough();
var LinkTriggeredActionSchema = z6.object({
  type: z6.literal("link"),
  settings: z6.object({
    url: z6.string().nullish()
  })
});
var TagTriggeredActionSchema = z6.object({
  type: z6.literal("tag"),
  settings: z6.object({
    partnerName: z6.string().optional(),
    tagIds: z6.array(z6.string()).optional(),
    thanksForConvertingText: z6.string().optional(),
    redirectFunnel: FunnelDefinitionSchema.optional(),
    formSettings: z6.record(z6.unknown()).optional(),
    fields: z6.array(FormFieldSchema).optional()
  })
});
var FormTriggeredActionSchema = z6.object({
  type: z6.literal("form"),
  settings: z6.object({
    partnerName: z6.string().optional(),
    formIds: z6.array(z6.string()).optional(),
    thanksForConvertingText: z6.string().optional(),
    redirectFunnel: FunnelDefinitionSchema.optional()
  })
});
var ListTriggeredActionSchema = z6.object({
  type: z6.literal("list"),
  settings: z6.object({
    partnerName: z6.string().optional(),
    listIds: z6.array(z6.string()).optional(),
    thanksForConvertingText: z6.string().optional(),
    redirectFunnel: FunnelDefinitionSchema.optional()
  })
});
var EventTriggeredActionSchema = z6.object({
  type: z6.literal("event"),
  settings: z6.object({
    partnerName: z6.string().optional(),
    eventIds: z6.union([z6.string(), z6.array(z6.string())]).optional(),
    thanksForConvertingText: z6.string().optional(),
    redirectFunnel: FunnelDefinitionSchema.optional()
  })
});
var TriggeredActionSchema = z6.discriminatedUnion("type", [
  LinkTriggeredActionSchema,
  TagTriggeredActionSchema,
  FormTriggeredActionSchema,
  ListTriggeredActionSchema,
  EventTriggeredActionSchema
]);
var OfferPartsSchema = z6.record(z6.string());
var OfferCreateDataSchema = z6.object({
  name: z6.string().min(1, "Offer name is required"),
  isEnabled: z6.boolean().default(true),
  parts: OfferPartsSchema.default({}),
  templateId: z6.string().optional(),
  triggeredAction: TriggeredActionSchema
});
var OfferDataSchema = z6.object({
  id: OfferIdSchema,
  name: z6.string().min(1),
  isEnabled: z6.boolean().default(true),
  accessedAt: z6.number().optional(),
  parts: OfferPartsSchema,
  templateId: z6.string().optional(),
  triggeredAction: TriggeredActionSchema
});
var OfferUpdateDataSchema = z6.object({
  name: z6.string().min(1).optional(),
  isEnabled: z6.boolean().optional(),
  parts: OfferPartsSchema.optional(),
  triggeredAction: TriggeredActionSchema.optional()
});

// src/resources/flowNode.schema.ts
import { z as z7 } from "zod";
var LogicDefinitionSchema2 = z7.record(z7.unknown());
var FlowNodeRunSchema = z7.lazy(() => z7.array(FlowNodeSchema));
var SplitBranchSchema = z7.object({
  segmentId: SegmentIdSchema.nullable(),
  child: FlowNodeRunSchema
});
var RandomSplitBranchSchema = z7.object({
  child: FlowNodeRunSchema
});
var HighscoreSplitBranchSchema = z7.object({
  score: z7.string().nullable(),
  child: FlowNodeRunSchema
});
var DataActionSchema = z7.object({
  type: z7.enum(["score", "field", "variable", "add_tag", "remove_tag"]),
  id: z7.string(),
  tagId: z7.string().optional()
}).passthrough();
var SgNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("sg"),
  segmentGroupId: z7.union([DimensionIdSchema, QuestionIdSchema, z7.null()]).optional(),
  ask: z7.boolean(),
  split: z7.union([z7.literal(false), z7.array(SplitBranchSchema)])
});
var RandomNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("random"),
  split: z7.array(RandomSplitBranchSchema)
});
var YesNoNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("yesno"),
  note: z7.string().optional(),
  condition: LogicDefinitionSchema2,
  yes: FlowNodeRunSchema,
  no: FlowNodeRunSchema
});
var OfferNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("offer"),
  desiredType: z7.enum(["form", "offer"]).optional(),
  offerId: z7.union([OfferIdSchema, z7.null()]).optional()
});
var RedirectNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("redirect"),
  url: z7.string(),
  open: z7.boolean()
});
var DataNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("data"),
  actions: z7.array(DataActionSchema)
});
var MessageNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("message"),
  content: z7.string()
});
var HighscoreNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("highscore"),
  split: z7.array(HighscoreSplitBranchSchema)
});
var CodeNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("code"),
  name: z7.string(),
  code: z7.string(),
  selector: z7.string().optional()
});
var ExitNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("exit")
});
var FlowNodeSchema = z7.discriminatedUnion("type", [
  SgNodeSchema,
  RandomNodeSchema,
  YesNoNodeSchema,
  OfferNodeSchema,
  RedirectNodeSchema,
  DataNodeSchema,
  MessageNodeSchema,
  HighscoreNodeSchema,
  CodeNodeSchema,
  ExitNodeSchema
]);
var RootNodeSchema = z7.object({
  _uuid: z7.string().optional(),
  type: z7.literal("root"),
  child: FlowNodeRunSchema
});
var FlowNodeTypeSchema = z7.enum([
  "sg",
  "random",
  "yesno",
  "offer",
  "redirect",
  "data",
  "message",
  "highscore",
  "code",
  "exit"
]);

// src/resources/flow.schema.ts
import { z as z8 } from "zod";
var flowIdPattern = /^ofn_[a-zA-Z0-9]+$/;
var flowTempIdPattern = /^ofn_tmp_\d+$/;
var FlowIdSchema = z8.string().refine(
  (id) => flowIdPattern.test(id) || flowTempIdPattern.test(id),
  "Invalid flow ID format"
);
var FlowCreateDataSchema = z8.object({
  name: z8.string().min(1, "Flow name is required"),
  templateId: z8.string().optional(),
  funnelDefinition: RootNodeSchema,
  isSystemFlow: z8.boolean().default(false)
});
var FlowDataSchema = z8.object({
  id: FlowIdSchema,
  name: z8.string().min(1),
  templateId: z8.string().optional(),
  accessedAt: z8.number().optional(),
  funnelDefinition: RootNodeSchema,
  isSystemFlow: z8.boolean().default(false)
});
var FlowUpdateDataSchema = z8.object({
  name: z8.string().min(1).optional(),
  funnelDefinition: RootNodeSchema.optional()
});

// src/resources/widget.schema.ts
import { z as z9 } from "zod";
var widgetIdPattern = /^wdg_[a-zA-Z0-9]+$/;
var widgetTempIdPattern = /^wdg_tmp_\d+$/;
var WidgetIdSchema = z9.string().refine(
  (id) => widgetIdPattern.test(id) || widgetTempIdPattern.test(id),
  "Invalid widget ID format"
);
var PageEntrySchema = z9.object({
  domain: z9.string().default("*"),
  path: z9.string().default("*")
});
var PageListSchema = z9.array(PageEntrySchema).default([]);
var DismissRuleSchema = z9.object({
  type: z9.enum(["session", "duration", "forever"]).default("session"),
  duration: z9.number().optional(),
  unit: z9.enum(["minutes", "hours", "days", "weeks"]).optional()
});
var WidgetVisibilitySchema = z9.object({
  isWhitelistEnabled: z9.boolean().default(false),
  whitelist: PageListSchema.default([]),
  isBlacklistEnabled: z9.boolean().default(true),
  blacklist: PageListSchema.default([]),
  trigger: z9.string().optional(),
  areas: z9.string().optional(),
  dismissRule: DismissRuleSchema.default({ type: "session" })
});
var WidgetOptionsSchema = z9.object({
  font: z9.string().optional(),
  theme: z9.string().optional(),
  palette: z9.record(z9.string()).optional(),
  fontSize: z9.record(z9.union([z9.string(), z9.number()])).optional(),
  customCss: z9.string().optional(),
  structure: z9.record(z9.union([z9.string(), z9.number()])).optional(),
  displayTargets: z9.object({
    phones: z9.boolean().default(true),
    desktop: z9.boolean().default(true),
    tablets: z9.boolean().default(true)
  }).optional(),
  backButtonLabel: z9.string().optional(),
  backButtonEnabled: z9.boolean().optional()
}).passthrough();
var WidgetTypeSchema = z9.enum([
  "inline",
  "popup",
  "modal",
  "bar",
  "slide",
  "sticky",
  "embed"
]);
var WhenToAskNextSchema = z9.enum([
  "startover",
  "immediately",
  "pageview",
  "session",
  "none"
]);
var WidgetCreateDataSchema = z9.object({
  name: z9.string().min(1, "Widget name is required"),
  type: WidgetTypeSchema.default("inline"),
  isEnabled: z9.boolean().default(true),
  options: WidgetOptionsSchema.default({}),
  whenToAskNext: WhenToAskNextSchema.default("immediately"),
  visibility: WidgetVisibilitySchema.default({}),
  contentId: z9.union([FlowIdSchema, OfferIdSchema, z9.null()]).optional()
});
var WidgetDataSchema = z9.object({
  id: WidgetIdSchema,
  name: z9.string().min(1),
  type: WidgetTypeSchema,
  accessedAt: z9.number().optional(),
  isEnabled: z9.boolean().default(true),
  options: WidgetOptionsSchema,
  whenToAskNext: WhenToAskNextSchema,
  visibility: WidgetVisibilitySchema,
  contentId: z9.union([FlowIdSchema, OfferIdSchema, z9.null()]).optional()
});
var WidgetUpdateDataSchema = z9.object({
  name: z9.string().min(1).optional(),
  type: WidgetTypeSchema.optional(),
  isEnabled: z9.boolean().optional(),
  options: WidgetOptionsSchema.optional(),
  whenToAskNext: WhenToAskNextSchema.optional(),
  visibility: WidgetVisibilitySchema.optional(),
  contentId: z9.union([FlowIdSchema, OfferIdSchema, z9.null()]).optional()
});

// src/resources/page.schema.ts
import { z as z10 } from "zod";
var pageIdPattern = /^\d+$/;
var pageTempIdPattern = /^pag_tmp_\d+$/;
var PageIdSchema = z10.string().refine(
  (id) => id === "current" || pageIdPattern.test(id) || pageTempIdPattern.test(id),
  "Invalid page ID format"
);
var PageStatusSchema = z10.enum(["draft", "published", "archived"]);
var PageCreateDataSchema = z10.object({
  name: z10.string().min(1, "Page name is required"),
  slug: z10.string().optional(),
  content: z10.record(z10.unknown()).default({}),
  settings: z10.record(z10.unknown()).default({}),
  flowId: z10.string().optional(),
  status: PageStatusSchema.default("draft")
});
var PageDataSchema = z10.object({
  id: z10.string(),
  pid: z10.string().optional(),
  name: z10.string().min(1),
  slug: z10.string().optional(),
  content: z10.record(z10.unknown()).default({}),
  settings: z10.record(z10.unknown()).default({}),
  flowId: z10.string().nullish(),
  status: PageStatusSchema.default("draft"),
  createdAt: z10.string().optional(),
  updatedAt: z10.string().optional()
});
var PageUpdateDataSchema = z10.object({
  name: z10.string().min(1).optional(),
  slug: z10.string().optional(),
  content: z10.record(z10.unknown()).optional(),
  settings: z10.record(z10.unknown()).optional(),
  flowId: z10.string().nullish().optional(),
  status: PageStatusSchema.optional()
});

// src/resources/email.schema.ts
import { z as z11 } from "zod";
var emailIdPattern = /^\d+$/;
var emailTempIdPattern = /^eml_tmp_\d+$/;
var EmailIdSchema = z11.string().refine(
  (id) => emailIdPattern.test(id) || emailTempIdPattern.test(id),
  "Invalid email ID format"
);
var EmailCreateDataSchema = z11.object({
  name: z11.string().min(1, "Email name is required"),
  originalEmail: z11.string().optional(),
  personalizedEmail: z11.string().optional(),
  instructions: z11.string().optional(),
  affectedSegments: z11.array(z11.string()).default([])
});
var EmailDataSchema = z11.object({
  id: z11.string(),
  pid: z11.string().optional(),
  name: z11.string().min(1),
  originalEmail: z11.string().nullish(),
  personalizedEmail: z11.string().nullish(),
  instructions: z11.string().nullish(),
  affectedSegments: z11.array(z11.string()).default([]),
  createdAt: z11.string().optional(),
  updatedAt: z11.string().optional()
});
var EmailUpdateDataSchema = z11.object({
  name: z11.string().min(1).optional(),
  originalEmail: z11.string().optional(),
  personalizedEmail: z11.string().optional(),
  instructions: z11.string().optional(),
  affectedSegments: z11.array(z11.string()).optional()
});

// src/jsonSchema.ts
var resourceTypes = [
  // Config resources
  "dimension",
  "segment",
  "question",
  "questionOption",
  "offer",
  "flow",
  "flow.node",
  "widget",
  "phrase",
  "campaign",
  "variant",
  "action",
  // Database resources
  "page",
  "email"
];
var createOperationSchema = {
  type: "object",
  required: ["op", "resource", "tempId", "data"],
  properties: {
    op: { const: "create" },
    resource: {
      type: "string",
      enum: resourceTypes.filter((r) => r !== "flow.node"),
      description: "Resource type to create"
    },
    tempId: {
      type: "string",
      description: "Temporary ID (e.g., dim_tmp_0, seg_tmp_0_0) - will be replaced with real ID",
      pattern: "^[a-z]+_tmp_\\d+(_\\d+)?$"
    },
    data: {
      type: "object",
      description: "Resource-specific data",
      additionalProperties: true
    }
  },
  additionalProperties: false
};
var updateOperationSchema = {
  type: "object",
  required: ["op", "resource", "id", "data"],
  properties: {
    op: { const: "update" },
    resource: {
      type: "string",
      enum: resourceTypes.filter((r) => r !== "flow.node"),
      description: "Resource type to update"
    },
    id: {
      type: "string",
      description: "Real ID of the existing resource"
    },
    data: {
      type: "object",
      description: "Partial resource data to merge",
      additionalProperties: true
    }
  },
  additionalProperties: false
};
var deleteOperationSchema = {
  type: "object",
  required: ["op", "resource", "id"],
  properties: {
    op: { const: "delete" },
    resource: {
      type: "string",
      enum: resourceTypes.filter((r) => r !== "flow.node"),
      description: "Resource type to delete"
    },
    id: {
      type: "string",
      description: "Real ID of the resource to delete"
    }
  },
  additionalProperties: false
};
var flowNodePositionSchema = {
  type: "object",
  properties: {
    parent: {
      type: "string",
      description: "Parent node UUID or temp ID"
    },
    after: {
      type: "string",
      description: "Insert after this node UUID or temp ID"
    },
    branch: {
      type: "string",
      description: "Branch identifier (segmentId or yes/no)"
    }
  },
  additionalProperties: false
};
var createFlowNodeOperationSchema = {
  type: "object",
  required: ["op", "resource", "flowId", "tempId", "position", "data"],
  properties: {
    op: { const: "create" },
    resource: { const: "flow.node" },
    flowId: {
      type: "string",
      description: "Flow ID (real or temp)"
    },
    tempId: {
      type: "string",
      description: "Temporary ID for this node",
      pattern: "^node_tmp_\\d+$"
    },
    position: flowNodePositionSchema,
    data: {
      type: "object",
      description: "Flow node data",
      additionalProperties: true
    }
  },
  additionalProperties: false
};
var updateFlowNodeOperationSchema = {
  type: "object",
  required: ["op", "resource", "flowId", "nodeUuid", "data"],
  properties: {
    op: { const: "update" },
    resource: { const: "flow.node" },
    flowId: {
      type: "string",
      description: "Flow ID"
    },
    nodeUuid: {
      type: "string",
      description: "UUID of the node to update"
    },
    data: {
      type: "object",
      description: "Partial node data to merge",
      additionalProperties: true
    }
  },
  additionalProperties: false
};
var deleteFlowNodeOperationSchema = {
  type: "object",
  required: ["op", "resource", "flowId", "nodeUuid"],
  properties: {
    op: { const: "delete" },
    resource: { const: "flow.node" },
    flowId: {
      type: "string",
      description: "Flow ID"
    },
    nodeUuid: {
      type: "string",
      description: "UUID of the node to delete"
    }
  },
  additionalProperties: false
};
var moveFlowNodeOperationSchema = {
  type: "object",
  required: ["op", "resource", "flowId", "nodeUuid", "position"],
  properties: {
    op: { const: "move" },
    resource: { const: "flow.node" },
    flowId: {
      type: "string",
      description: "Flow ID"
    },
    nodeUuid: {
      type: "string",
      description: "UUID of the node to move"
    },
    position: flowNodePositionSchema
  },
  additionalProperties: false
};
var operationSchema = {
  oneOf: [
    createOperationSchema,
    updateOperationSchema,
    deleteOperationSchema,
    createFlowNodeOperationSchema,
    updateFlowNodeOperationSchema,
    deleteFlowNodeOperationSchema,
    moveFlowNodeOperationSchema
  ]
};
var planToolSchema = {
  type: "object",
  required: ["operations"],
  properties: {
    title: {
      type: "string",
      description: "Brief title describing what the plan accomplishes"
    },
    description: {
      type: "string",
      description: "Detailed description of the changes being made"
    },
    operations: {
      type: "array",
      description: "List of CRUD operations to execute in order",
      items: operationSchema,
      minItems: 1
    }
  },
  additionalProperties: false
};
var simplifiedPlanToolSchema = {
  type: "object",
  required: ["operations"],
  properties: {
    title: {
      type: "string",
      description: 'Brief title for the plan (e.g., "Create Business Type Survey")'
    },
    description: {
      type: "string",
      description: "What this plan accomplishes"
    },
    operations: {
      type: "array",
      description: "List of CRUD operations to execute",
      items: {
        type: "object",
        required: ["op", "resource"],
        properties: {
          op: {
            type: "string",
            enum: ["create", "update", "delete", "move"],
            description: "Operation type"
          },
          resource: {
            type: "string",
            enum: [
              // Config resources
              "dimension",
              "segment",
              "question",
              "questionOption",
              "offer",
              "flow",
              "flow.node",
              "widget",
              "phrase",
              "campaign",
              "variant",
              "action",
              // Database resources
              "page",
              "email"
            ],
            description: "Resource type"
          },
          tempId: {
            type: "string",
            description: "Temporary ID for create operations (e.g., dim_tmp_0, seg_tmp_0_0)"
          },
          id: {
            type: "string",
            description: "Real ID for update/delete operations"
          },
          flowId: {
            type: "string",
            description: "Flow ID for flow.node operations"
          },
          nodeUuid: {
            type: "string",
            description: "Node UUID for flow.node update/delete/move"
          },
          position: {
            type: "object",
            description: "Position for flow.node create/move operations",
            properties: {
              parent: { type: "string" },
              after: { type: "string" },
              branch: { type: "string" }
            }
          },
          data: {
            type: "object",
            description: "Resource data for create/update operations"
          }
        }
      },
      minItems: 1
    }
  }
};
function getPlanToolSchemaJson() {
  return JSON.stringify(simplifiedPlanToolSchema, null, 2);
}
export {
  AnyOperationSchema,
  AnyPlanSchema,
  BaseCreateOperationSchema,
  BaseDeleteOperationSchema,
  BaseUpdateOperationSchema,
  CodeNodeSchema,
  CreateFlowNodeOperationSchema,
  DataActionSchema,
  DataNodeSchema,
  DeleteFlowNodeOperationSchema,
  DimensionCreateDataSchema,
  DimensionDataSchema,
  DimensionIdSchema,
  DimensionUpdateDataSchema,
  DismissRuleSchema,
  DisplayOptionsSchema,
  EmailCreateDataSchema,
  EmailDataSchema,
  EmailIdSchema,
  EmailUpdateDataSchema,
  ExitNodeSchema,
  FlowCreateDataSchema,
  FlowDataSchema,
  FlowIdSchema,
  FlowNodePositionSchema,
  FlowNodeSchema,
  FlowNodeTypeSchema,
  FlowUpdateDataSchema,
  FormFieldSchema,
  HighscoreNodeSchema,
  InputSettingsSchema,
  LegacyPlanSchema,
  LegacyStepSchema,
  MessageNodeSchema,
  MoveFlowNodeOperationSchema,
  OfferCreateDataSchema,
  OfferDataSchema,
  OfferIdSchema,
  OfferNodeSchema,
  OfferPartsSchema,
  OfferUpdateDataSchema,
  OperationResultSchema,
  OtherOptionsSchema,
  PageCreateDataSchema,
  PageDataSchema,
  PageEntrySchema,
  PageIdSchema,
  PageListSchema,
  PageStatusSchema,
  PageUpdateDataSchema,
  PlanExecutionResultSchema,
  PlanMetadataSchema,
  PlanSchema,
  QuestionCreateDataSchema,
  QuestionDataSchema,
  QuestionIdSchema,
  QuestionOptionCreateDataSchema,
  QuestionOptionDataSchema,
  QuestionOptionIdSchema,
  QuestionUpdateDataSchema,
  RedirectNodeSchema,
  ResourceTypeSchema,
  RootNodeSchema,
  SegmentCreateDataSchema,
  SegmentDataSchema,
  SegmentIdSchema,
  SegmentUpdateDataSchema,
  SgNodeSchema,
  SignalDefinitionSchema,
  SignalSchema,
  SplitBranchSchema,
  TriggeredActionSchema,
  UpdateFlowNodeOperationSchema,
  WhenToAskNextSchema,
  WidgetCreateDataSchema,
  WidgetDataSchema,
  WidgetIdSchema,
  WidgetOptionsSchema,
  WidgetTypeSchema,
  WidgetUpdateDataSchema,
  WidgetVisibilitySchema,
  YesNoNodeSchema,
  buildIdMapFromOperations,
  createFlowNodeOperationSchema,
  createOperationSchema,
  createTempIdCounters,
  deleteFlowNodeOperationSchema,
  deleteOperationSchema,
  flowNodePositionSchema,
  generateTempId,
  getPlanToolSchemaJson,
  getResourceTypeFromId,
  idOrTempPatterns,
  idPatterns,
  idPrefixes,
  isIdOrTempId,
  isLegacyPlan,
  isOperationsPlan,
  isRealId,
  isTempId,
  moveFlowNodeOperationSchema,
  operationSchema,
  planToolSchema,
  resolveId,
  resolveTempIds,
  resourceTypes,
  simplifiedPlanToolSchema,
  tempIdPatterns,
  updateFlowNodeOperationSchema,
  updateOperationSchema,
  validateTempIdReferences
};
//# sourceMappingURL=index.js.map